/**
 * COSFY Data Hooks
 * 
 * Each hook fetches from the live API, tracks loading/error state,
 * and falls back to mock data when the backend is unreachable.
 * This lets the frontend work in demo mode (no backend) and
 * production mode (wired to FastAPI) with the same components.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import * as api from "./api.js";

// ── Generic fetch hook ─────────────────────────────────────────

export function useApi(fetcher, deps = [], fallback = null) {
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLive, setIsLive] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      setData(result);
      setIsLive(true);
    } catch (err) {
      setError(err);
      setIsLive(false);
      if (fallback !== null) setData(fallback);
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => { reload(); }, [reload]);

  return { data, loading, error, isLive, reload, setData };
}

// ── Auth Hook ──────────────────────────────────────────────────

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);

  const checkAuth = useCallback(async () => {
    if (!api.hasToken()) { setLoading(false); return; }
    try {
      const me = await api.getMe();
      setUser(me);
      setIsLive(true);
    } catch {
      // Token invalid or backend down — clear and go to login
      api.clearTokens();
      setIsLive(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const tokens = await api.login(email, password);
    const me = await api.getMe();
    setUser(me);
    setIsLive(true);
    return me;
  };

  const loginDemo = () => {
    // Demo mode — fake user, no backend needed
    setUser({
      id: "demo",
      email: "ag@aarama.in",
      name: "AG",
      display_name: "AG",
      title: "Founder",
      department: "Leadership",
      role: "admin",
      org_id: "demo-org",
    });
    setIsLive(false);
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
    setIsLive(false);
  };

  return { user, loading, isLive, login, loginDemo, logout };
}

// ── Search Hook ────────────────────────────────────────────────

export function useSearch(query, sources = [], orgId = "") {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLive, setIsLive] = useState(false);

  const doSearch = useCallback(async () => {
    if (!query?.trim()) { setResults(null); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await api.search(query, {
        orgId,
        sources: sources.length && sources[0] !== "all" ? sources : [],
      });
      setResults(data);
      setIsLive(true);
    } catch (err) {
      setError(err);
      setIsLive(false);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [query, sources.join(","), orgId]);

  return { results, loading, error, isLive, doSearch };
}

// ── Chat Hook with SSE streaming ───────────────────────────────

export function useChat() {
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const abortRef = useRef(null);

  const loadConversations = async () => {
    try {
      const data = await api.listConversations();
      setConversations(data.conversations || data || []);
      setIsLive(true);
    } catch {
      setIsLive(false);
    }
  };

  const startConversation = async () => {
    try {
      const conv = await api.createConversation();
      setActiveConv(conv);
      setMessages([]);
      setIsLive(true);
      return conv;
    } catch {
      // Demo mode — fake conversation
      const fake = { id: `demo-${Date.now()}`, title: "New conversation" };
      setActiveConv(fake);
      setMessages([]);
      setIsLive(false);
      return fake;
    }
  };

  const sendStreaming = async (content) => {
    if (!activeConv) return;
    
    // Append user message immediately
    const userMsg = { id: Date.now(), role: "user", content, sources: [] };
    setMessages(prev => [...prev, userMsg]);
    setStreaming(true);

    // Create placeholder for assistant response
    const assistantId = Date.now() + 1;
    const assistantMsg = { id: assistantId, role: "assistant", content: "", sources: [] };
    setMessages(prev => [...prev, assistantMsg]);

    try {
      const res = await api.streamMessage(activeConv.id, content);
      if (!res.ok) throw new Error("Stream failed");
      
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            const eventType = line.slice(7).trim();
            continue;
          }
          if (line.startsWith("data: ")) {
            try {
              const payload = JSON.parse(line.slice(6));
              if (payload.text !== undefined) {
                // Chunk event
                setMessages(prev => prev.map(m =>
                  m.id === assistantId
                    ? { ...m, content: m.content + payload.text }
                    : m
                ));
              }
              if (payload.title) {
                // Source event
                setMessages(prev => prev.map(m =>
                  m.id === assistantId
                    ? { ...m, sources: [...m.sources, payload] }
                    : m
                ));
              }
            } catch {}
          }
        }
      }
      setIsLive(true);
    } catch {
      // Fallback: non-streaming send
      try {
        const response = await api.sendMessage(activeConv.id, content);
        setMessages(prev => prev.map(m =>
          m.id === assistantId
            ? { ...m, content: response.assistant_message?.content || response.content || "I found relevant information in your connected tools.", sources: response.assistant_message?.sources || response.sources || [] }
            : m
        ));
        setIsLive(true);
      } catch {
        // Full offline — mock response
        setMessages(prev => prev.map(m =>
          m.id === assistantId
            ? { ...m, content: "I'm currently in demo mode. When connected to the COSFY backend, I'll search across all 20 connected tools to answer your questions with citations.\n\nTo connect: start the FastAPI backend on port 8000." }
            : m
        ));
        setIsLive(false);
      }
    } finally {
      setStreaming(false);
    }
  };

  return {
    conversations, activeConv, messages, streaming, isLive,
    loadConversations, startConversation, sendStreaming,
    setMessages, setActiveConv,
  };
}

// ── Polling hook for live data ──────────────────────────────────

export function usePolling(fetcher, intervalMs, fallback = null) {
  const state = useApi(fetcher, [], fallback);
  
  useEffect(() => {
    const id = setInterval(() => state.reload(), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return state;
}
